import { InfoTip } from "@/components/ui/info-tip";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, History, Pencil, Plus, Trash2, Workflow } from "lucide-react";
import { ApiError } from "@/lib/api";
import {
  deleteAutomation,
  getAutomation,
  getAutomationCatalog,
  listAutomations,
  resumeAutomation,
  setAutomationEnabled,
  setAutomationMode,
  type AutomationCatalog,
  type AutomationDetail,
  type AutomationRunMode,
  type AutomationSummary,
} from "@/lib/automation";
import { getReference, type Reference } from "@/lib/tickets";
import { Permission, useCan } from "@/components/session-provider";
import { ListSearch } from "@/components/ListSearch";
import { ConfirmPanel } from "@/components/Confirm";
import { RelativeTime } from "@/components/RelativeTime";
import { RuleEditor } from "@/components/automation/RuleEditor";
import { RunHistory } from "@/components/automation/RunHistory";
import { cn } from "@/lib/utils";

/**
 * Off, watching, or acting — the three states a rule can be in (D-164).
 *
 * ⚠️ Derived from the two server-side fields rather than stored as a third: `IsEnabled` and
 * `Mode` answer different questions and each is set on its own route, so a stored tri-state
 * would be a third opinion for the other two to disagree with.
 */
type RuleState = "off" | "whatif" | "live";

function ruleState(rule: AutomationSummary): RuleState {
  if (!rule.isEnabled) return "off";
  return rule.mode === "WhatIf" ? "whatif" : "live";
}

export const Route = createFileRoute("/automation")({
  component: AutomationPage,
});

/**
 * The rules that act on requests with nobody pressing anything (D-146).
 *
 * ⚠️ Reaching this screen is `automation.manage` — Owner and Super Admin — and that is a
 * courtesy on this side: every route under /api/automation re-checks. What this page does is
 * make a standing decision legible enough to get right: what fires it, what it looks at, what
 * it does, and what it has actually been doing.
 *
 * ⚠️ Three switches, and the page says so. The feature flag stops every rule; a rule's own
 * switch stops one; a suspension is the runner having stopped one that went wrong, and it is
 * shown with its reason rather than folded into "off" — turning a suspended rule back on must
 * not quietly hand it back a permission its owner lost.
 */
function AutomationPage() {
  const allowed = useCan(Permission.AutomationManage);

  const [rules, setRules] = useState<AutomationSummary[] | null>(null);
  const [catalog, setCatalog] = useState<AutomationCatalog | null>(null);
  const [reference, setReference] = useState<Reference | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  const [editing, setEditing] = useState<AutomationDetail | null | "new">(null);
  const [showingRuns, setShowingRuns] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<AutomationSummary | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      setRules(await listAutomations());
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load the rules.");
      setRules([]);
    }
  }

  useEffect(() => {
    if (!allowed) return;

    void load();
    void getAutomationCatalog()
      .then(setCatalog)
      .catch(() => setCatalog(null));
    void getReference()
      .then(setReference)
      .catch(() =>
        setReference({
          teams: [],
          sites: [],
          sourceApps: [],
          assistantName: "Nova",
          assistantGreeting: null,
          assistantShowInHeader: true,
        }),
      );
  }, [allowed]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || !rules) return rules ?? [];
    return rules.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        (r.description ?? "").toLowerCase().includes(q) ||
        r.trigger.toLowerCase().includes(q) ||
        r.ownerName.toLowerCase().includes(q),
    );
  }, [rules, query]);

  /**
   * The three states a rule can be in, as one control.
   *
   * ⚠️ Two server-side fields, one gesture — and the ORDER matters. The mode is set first, so a
   * rule going from Off straight to Live is never briefly enabled in the mode it was left in;
   * setting the mode on a switched-off rule changes nothing anybody can observe. The reverse
   * order has a window, however short, where the rule is running under the wrong one.
   */
  async function setState(rule: AutomationSummary, state: RuleState) {
    const wantsEnabled = state !== "off";
    const wantsMode: AutomationRunMode = state === "live" ? "Live" : "WhatIf";

    setBusy(true);
    setError(null);
    try {
      if (rule.mode !== wantsMode) await setAutomationMode(rule.id, wantsMode);
      if (rule.isEnabled !== wantsEnabled) await setAutomationEnabled(rule.id, wantsEnabled);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "That change was not saved.");
    } finally {
      setBusy(false);
    }
  }

  async function resume(rule: AutomationSummary) {
    setBusy(true);
    setError(null);
    try {
      await resumeAutomation(rule.id);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "That rule was not resumed.");
    } finally {
      setBusy(false);
    }
  }

  async function remove(rule: AutomationSummary) {
    setBusy(true);
    setError(null);
    try {
      await deleteAutomation(rule.id);
      setDeleting(null);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "That rule was not deleted.");
    } finally {
      setBusy(false);
    }
  }

  async function edit(rule: AutomationSummary) {
    setError(null);
    try {
      setEditing(await getAutomation(rule.id));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not open that rule.");
    }
  }

  if (!allowed) {
    return (
      <div className="mx-auto max-w-3xl p-4 sm:p-6">
        <p className="text-sm text-muted-foreground">This screen is not available to you.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <Workflow className="h-5 w-5" /> Automation
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            When something happens to a request, check conditions and take actions — without anybody
            pressing anything. A rule can only do what its owner could do by hand.
          </p>
        </div>
        {editing === null && catalog && reference && (
          <button
            type="button"
            onClick={() => setEditing("new")}
            className="inline-flex h-10 items-center gap-2 rounded-lg bg-gradient-primary px-4 text-sm font-medium text-primary-foreground transition hover:opacity-90"
          >
            <Plus className="h-4 w-4" /> New rule
          </button>
        )}
      </header>

      <p className="mt-4 rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
        Rules run only while{" "}
        <strong className="font-medium text-foreground">Automation rules</strong> is switched on
        under Settings → Features. It ships off. Each rule also has its own switch, and a rule the
        service desk stops on its own is shown here with the reason.
      </p>

      {/*
        ⚠️ It says what-if changes NOTHING, in those words. A rule that is running, matching and
        filling its history with what it would have done is indistinguishable from a working rule
        unless the screen is explicit — and somebody who believes a what-if rule is acting will
        wait for it to act.
      */}
      <p className="mt-2 rounded-md bg-sky-500/10 px-3 py-2 text-xs text-sky-800 dark:text-sky-200">
        A new rule starts <strong className="font-medium">off</strong>, and switching it on puts it
        in <strong className="font-medium">what-if</strong>: it is checked against everything that
        happens and the run history records exactly what it would have done, while it changes
        nothing at all. Read a few days of that, then put it live.
      </p>

      {error && (
        <div
          role="alert"
          className="mt-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm"
        >
          {error}
        </div>
      )}

      {editing !== null && catalog && reference && (
        <div className="mt-4">
          <RuleEditor
            catalog={catalog}
            reference={reference}
            initial={editing === "new" ? null : editing}
            onSaved={() => {
              setEditing(null);
              void load();
            }}
            onCancel={() => setEditing(null)}
          />
        </div>
      )}

      {editing === null && (
        <>
          {rules && rules.length > 0 && (
            <div className="mt-4">
              <ListSearch
                value={query}
                onChange={setQuery}
                label="rules"
                placeholder="Search rules"
                total={rules.length}
                showing={visible.length}
              />
            </div>
          )}

          {rules === null ? (
            <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
          ) : rules.length === 0 ? (
            <p className="mt-4 text-sm text-muted-foreground">
              No rules yet. A first one worth writing: when a request is raised in a category,
              assign it to whoever on that team has the fewest open requests.
            </p>
          ) : (
            <ul className="mt-4 space-y-3">
              {visible.map((rule) => (
                <li
                  key={rule.id}
                  className="group/card rounded-xl border border-border bg-card p-4"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-base font-semibold">{rule.name}</h2>
                        <InfoTip id="automation-rule" className="-ml-1" />
                        {/*
                          ⚠️ FOUR STATES, NOT THREE, and "What-if" has to read differently from
                          "Live" at a glance. A rule that is running and changing nothing looks
                          identical to one that is changing everything if both just say "On" —
                          and the whole value of what-if is being able to tell, on the list,
                          without opening anything.
                        */}
                        <span
                          className={cn(
                            "rounded-full px-2 py-0.5 text-xs font-medium",
                            rule.suspendedAtUtc
                              ? "bg-destructive/15 text-destructive"
                              : !rule.isEnabled
                                ? "bg-muted text-muted-foreground"
                                : rule.mode === "WhatIf"
                                  ? "bg-sky-500/15 text-sky-700 dark:text-sky-300"
                                  : "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
                          )}
                        >
                          {rule.suspendedAtUtc
                            ? "Suspended"
                            : !rule.isEnabled
                              ? "Off"
                              : rule.mode === "WhatIf"
                                ? "What-if"
                                : "Live"}
                        </span>
                      </div>
                      {rule.description && (
                        <p className="mt-1 text-sm text-muted-foreground">{rule.description}</p>
                      )}
                      <p className="mt-1 text-xs text-muted-foreground">
                        {triggerLabel(catalog, rule.trigger)} · {rule.conditionCount}{" "}
                        {rule.conditionCount === 1 ? "condition" : "conditions"} ·{" "}
                        {rule.actionCount} {rule.actionCount === 1 ? "action" : "actions"} · owner{" "}
                        {rule.ownerName}
                      </p>
                      {rule.trigger === "IntakeDraft" ? (
                        // Intake rules are never "applied" — they are shown, and either settle
                        // the matter or do not (D-160). Both numbers, so the rate has its
                        // denominator beside it (D-53); counts, never who.
                        <p className="mt-1 text-xs text-muted-foreground">
                          Shown {rule.shownCount} {rule.shownCount === 1 ? "time" : "times"} ·
                          answered it {rule.answeredCount}{" "}
                          {rule.answeredCount === 1 ? "time" : "times"}
                        </p>
                      ) : (
                        // ⚠️ "Would have applied" for a rule in what-if. The figure is the same
                        // count either way, and printing "Applied 143 times" beside a rule that
                        // has changed nothing is the product claiming something untrue about its
                        // own data — the defect D-137 found in the satisfaction report's copy.
                        <p className="mt-1 text-xs text-muted-foreground">
                          {rule.mode === "WhatIf" ? "Would have applied" : "Applied"}{" "}
                          {rule.appliedInLastDay} {rule.appliedInLastDay === 1 ? "time" : "times"}{" "}
                          in the last day
                          {rule.lastRunAtUtc && (
                            <>
                              {" "}
                              · last evaluated <RelativeTime value={rule.lastRunAtUtc} />
                            </>
                          )}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-1">
                      {/*
                        ⚠️ ONE CONTROL OVER TWO FIELDS, rather than a switch and a mode toggle
                        sitting beside each other. Off / What-if / Live is the decision somebody
                        is actually making, and two adjacent controls is the shape where a rule
                        ends up enabled in a mode nobody chose (D-101).
                      */}
                      <label className="sr-only" htmlFor={`state-${rule.id}`}>
                        What this rule does
                      </label>
                      <select
                        id={`state-${rule.id}`}
                        className="h-8 rounded-md border border-input bg-background px-2 text-base md:text-xs"
                        value={ruleState(rule)}
                        disabled={busy || rule.suspendedAtUtc !== null}
                        onChange={(e) => void setState(rule, e.target.value as RuleState)}
                      >
                        <option value="off">Off</option>
                        <option value="whatif">What-if</option>
                        <option value="live">Live</option>
                      </select>
                      <IconButton
                        label="Run history"
                        onClick={() => setShowingRuns((c) => (c === rule.id ? null : rule.id))}
                      >
                        <History className="h-4 w-4" />
                      </IconButton>
                      <IconButton label="Edit" onClick={() => void edit(rule)}>
                        <Pencil className="h-4 w-4" />
                      </IconButton>
                      <IconButton label="Delete" onClick={() => setDeleting(rule)} disabled={busy}>
                        <Trash2 className="h-4 w-4" />
                      </IconButton>
                    </div>
                  </div>

                  {rule.suspendedAtUtc && (
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
                      <span className="flex items-start gap-2">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                        <span>
                          The service desk stopped this rule: {rule.suspendedReason}{" "}
                          <span className="text-muted-foreground">
                            (<RelativeTime value={rule.suspendedAtUtc} />)
                          </span>
                        </span>
                      </span>
                      <button
                        type="button"
                        onClick={() => void resume(rule)}
                        disabled={busy}
                        className="rounded-md border border-input bg-background px-3 py-1 text-xs font-medium"
                      >
                        Resume
                      </button>
                    </div>
                  )}

                  {deleting?.id === rule.id && (
                    <div className="mt-3">
                      <ConfirmPanel
                        question={`Delete "${rule.name}"?`}
                        detail="It stops running immediately. Its run history is kept so the record of what it did stays readable."
                        confirmLabel="Delete rule"
                        busy={busy}
                        onConfirm={() => void remove(rule)}
                        onCancel={() => setDeleting(null)}
                      />
                    </div>
                  )}

                  {showingRuns === rule.id && (
                    <div className="mt-3 border-t border-border pt-3">
                      <RunHistory automationId={rule.id} />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function triggerLabel(catalog: AutomationCatalog | null, trigger: string): string {
  return catalog?.triggers.find((t) => t.value === trigger)?.label ?? trigger;
}

function IconButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition hover:bg-muted hover:text-foreground disabled:opacity-50"
    >
      {children}
    </button>
  );
}
